import dayjs from 'dayjs';
import { Omit } from 'react-redux';
import * as api from '.';
import { gridQuery } from '.';
import { CallBookmark, EventSubscription, SERVER_DATE_FOMATE_MILL, TagType, TranscriptionCall, TranscriptionParagraph } from '../model';

export enum Sentiment {
  none = 0,
  negative = 1,
  neutral = 2,
  positive = 3,
  mixed = 4,
}

export interface AsyncStringResponse extends AsyncResponse {
  data: string,
}

export interface AsyncBookitResponse extends AsyncResponse {
  data: {
    url: string,
    token: string,
  },
}

export interface AsyncResponse {
  error: string,
  success: boolean,
}

export interface SetCallTranscriptionTag extends AsyncResponse {
  data: CallBookmark,
}


// interface TranscriptionData extends AsyncResponse {
//   data: Transcription,
// }

interface TranscriptionDataRaw extends AsyncResponse {
  data: TranscriptionRaw,
}

interface GetTranscriptionCallData extends AsyncResponse {
  data: TranscriptionCallRaw,
}

interface GetTranscriptionBookMarkData extends AsyncResponse {
  data: CallBookmarkRaw[],
}

export interface TranscriptionRaw {
  paragraphs: TranscriptionParagraphRaw[]
  calls: TranscriptionCallRaw[]
}

export interface MsDateTimes {
  createdAtUtc: string,
  modifiedAtUtc: string
}

export interface TranscriptionParagraphRaw extends Omit<TranscriptionParagraph, 'createdAtUtc' | 'modifiedAtUtc'> {
  createdAtUtc: string,
  modifiedAtUtc: string
}

export interface TranscriptionCallRaw extends Omit<TranscriptionCall, 'createdAtUtc' | 'modifiedAtUtc'> {
  createdAtUtc: string,
  modifiedAtUtc: string
}

export interface CallBookmarkRaw extends Omit<CallBookmark, 'createdAt' | 'modifiedAt' | 'timeStamp' | 'modifiedAt'> {
  createdAt: string,
  modifiedAt: string,
  timeStamp: string
}

async function callTranscriptionHandler<T>(command: string, params: Record<string, string | undefined>) {
  return api.parseJson<T>(await callTranscriptionHandlerRaw(command, params))
}

async function callTranscriptionHandlerRaw(command: string, params: Record<string, string | undefined>) {
  return await api.handler('Handlers/CallTranscription.ashx', {
    command, ...params,
  }, false)
}

export async function getCallTranscriptions(mediaId: string, page: number) {

  const grid = await gridQuery('transcription', '', '', EventSubscription.Add, mediaId, '', '', '', 50, undefined, page)

  return {
    ...grid, rows: grid.rows.map<TranscriptionParagraph>(row => {

      const createdAtUtc = dayjs(api.parseMsDate(row.createdAtUtc))
      const modifiedAtUtc = dayjs(api.parseMsDate(row.modifiedAtUtc))

      return {
        paragraphId: row.paragraphId,
        callId: row.callId,
        createdAtUtc: createdAtUtc.add(createdAtUtc.utcOffset(), 'minute').valueOf(),
        modifiedAtUtc: modifiedAtUtc.add(modifiedAtUtc.utcOffset(), 'minute').valueOf(),
        modifiedBy: row.modifiedBy,
        text: row.text,
        canEdit: row.canEdit,

        sentiment: row.sentiment, negative: row.negativeScore, neutral: row.neutralScore, positive: row.positiveScore,

        extensionId: row.extensionId,
        displayName: row.displayName,
        contactId: row.contactId,
        telephoneNumber: row.telephoneNumber,
        tags: [],
      }
    }),
  }
}

export async function deleteCallTranscriptionTag(tag: string) {
  return await callTranscriptionHandler<AsyncResponse>('delete-tag', { id: tag })
}

export async function setCallTranscriptionTag(medaiId: string, tag: string, type: TagType, timeStamp?: number) {
  return await callTranscriptionHandler<AsyncResponse>('set-tag', { id: medaiId, data: tag, type: type.toString(), date: timeStamp !== undefined ? dayjs(timeStamp).utc().format(SERVER_DATE_FOMATE_MILL) : '' })
}

export async function setCallTranscriptionTagTitle(bookmarkId: string, tag: string) {
  return await callTranscriptionHandler<AsyncResponse>('set-tag-title', { id: bookmarkId, data: tag })
}

export async function setCallTranscriptionText(paragraphId: string, text: string) {
  return await callTranscriptionHandler<AsyncStringResponse>('set-text', { id: paragraphId, data: text })
}

export async function callTranscriptionValidate(medaiId: string) {
  return await callTranscriptionHandler<AsyncStringResponse>('validate', { id: medaiId })
}

export async function callTranscriptionSubscribe(medaiId: string) {
  const request = await callTranscriptionHandler<TranscriptionDataRaw>('subscribe', { id: medaiId })

  if (request.success) {
    const calls = request.data.calls.map(x => {
      return {
        ...x,
        createdAtUtc: (typeof x.createdAtUtc === 'string') ? api.parseMsDate(x.createdAtUtc) || 0 : x.createdAtUtc,
        modifiedAtUtc: (typeof x.modifiedAtUtc === 'string') ? api.parseMsDate(x.modifiedAtUtc) || 0 : x.modifiedAtUtc,
      }
    })

    const paragraphs = request.data.paragraphs.map(x => {
      const call = calls.find(z => z.callId === x.callId)
      return {
        ...x,
        createdAtUtc: (typeof x.createdAtUtc === 'string') ? api.parseMsDate(x.createdAtUtc) || 0 : x.createdAtUtc,
        modifiedAtUtc: (typeof x.modifiedAtUtc === 'string') ? api.parseMsDate(x.modifiedAtUtc) || 0 : x.modifiedAtUtc,
        contactId: call ? call.contactId : undefined,
        displayName: call ? call.displayName : undefined,
        extensionId: call ? call.extensionId : undefined,
        telephoneNumber: call ? call.telephoneNumber : undefined,
      }
    })

    return {
      ...request,
      data: {
        paragraphs,
        calls,
      },
    }
  }

  return null
}

export async function callTranscriptionUnSubscribe(medaiId: string) {
  await callTranscriptionHandler('unsubscribe', { id: medaiId })
}

export async function getCallTranscriptionCall(medaiId: string, callTransactionId: string, active: boolean) {
  const request = await callTranscriptionHandler<GetTranscriptionCallData>('get-call', { id: medaiId, data: callTransactionId, type: (active ? 'live' : 'historical') })

  if (request.success) {
    return {
      callId: request.data.callId,
      callTransactionId: request.data.callTransactionId,
      createdAtUtc: (typeof request.data.createdAtUtc === 'string') ? api.parseMsDate(request.data.createdAtUtc) || 0 : request.data.createdAtUtc,
      modifiedAtUtc: (typeof request.data.modifiedAtUtc === 'string') ? api.parseMsDate(request.data.modifiedAtUtc) || 0 : request.data.modifiedAtUtc,
      partyTypeId: request.data.partyTypeId,
      displayName: request.data.displayName,
      telephoneNumber: request.data.telephoneNumber,
    } as TranscriptionCall
  }

  return undefined
}

export async function getCallTranscriptionBookmarks(medaiId: string) {
  const request = await callTranscriptionHandler<GetTranscriptionBookMarkData>('get-tags', { id: medaiId })

  if (request.success) {
    return request.data.map<CallBookmark>(x => ({
      bookmarkId: x.bookmarkId,
      title: x.title,
      timeStamp: (typeof x.timeStamp === 'string') ? api.parseMsDate(x.timeStamp) || 0 : x.timeStamp,
      bookmarkTypeId: x.bookmarkTypeId,
      createdBy: x.createdBy,
      createdAt: (typeof x.createdAt === 'string') ? api.parseMsDate(x.createdAt) || 0 : x.createdAt,
      modifiedBy: x.modifiedBy,
      modifiedAt: (typeof x.modifiedAt === 'string') ? api.parseMsDate(x.modifiedAt) || 0 : x.modifiedAt,
    }))
  }

  return undefined
}

export async function setParagraphSentiment(paragraphId: string, sentiment: Sentiment) {
  return await callTranscriptionHandler<AsyncStringResponse>('sentiment-p', { id: paragraphId, data: sentiment.toString() })
}

export async function setCallSentiment(mediaId: string, sentiment: Sentiment) {
  return await callTranscriptionHandler<AsyncStringResponse>('sentiment-c', { id: mediaId, data: sentiment.toString() })
}
